import { Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Actions, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import * as AuthActions from '../../store/actions/auth.actions';
import { getUser } from '../../store/selectors/auth.selectors';
import {
  getPlatformAccessPendingMessage,
  isPlatformAccessBlocked,
  normalizeRoutePath,
} from '../../utils/access-redirect';

/** Tempo máximo aguardando o /me antes de liberar o botão novamente. */
const VERIFY_TIMEOUT = 15000;
/** Tempo que a confirmação de liberação fica visível antes de fechar o modal. */
const RELEASE_DELAY = 5000;

const PUBLIC_ROUTES = [
  '/login',
  '/logout',
  '/sair',
  '/fremium',
  '/assinatura-suspensa',
  '/dados-cadastro',
  '/migracao-conta',
];

@Component({
  selector: 'platform-access-blocked-modal',
  templateUrl: './platform-access-blocked-modal.component.html',
  styleUrls: ['./platform-access-blocked-modal.component.scss'],
})
export class PlatformAccessBlockedModalComponent implements OnInit, OnDestroy {
  isOpen = false;
  isReleased = false;
  isVerifying = false;
  hasVerified = false;

  private userBlocked = false;
  private user: any = null;
  private previousOverflow = '';
  private verifyTimeout: any = null;
  private releaseTimeout: any = null;
  private destroy$ = new Subject<void>();

  constructor(private store: Store, private actions$: Actions, private router: Router) {}

  ngOnInit(): void {
    this.store
      .pipe(select(getUser), takeUntil(this.destroy$))
      .subscribe((user) => {
        this.user = user;
        this.syncBlockedState();
      });

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => this.syncBlockedState());

    this.actions$
      .pipe(
        ofType(AuthActions.accountSuccess, AuthActions.accountFailure),
        takeUntil(this.destroy$)
      )
      .subscribe(() => this.finishVerification());
  }

  ngOnDestroy(): void {
    this.clearVerifyTimeout();
    this.clearReleaseTimeout();
    this.unlockBodyScroll();
    this.destroy$.next();
    this.destroy$.complete();
  }

  checkAgain(): void {
    if (this.isVerifying) {
      return;
    }

    this.isVerifying = true;
    this.clearVerifyTimeout();

    this.store.dispatch(AuthActions.accountRetrieve());
    this.verifyTimeout = setTimeout(() => this.finishVerification(), VERIFY_TIMEOUT);
  }

  logout(): void {
    this.router.navigate(['/logout']);
  }

  getPendingMessage(): string {
    return getPlatformAccessPendingMessage(this.user);
  }

  private syncBlockedState(): void {
    const wasUserBlocked = this.userBlocked;
    this.userBlocked = isPlatformAccessBlocked(this.user);

    // Rotas públicas (login, logout, fluxo freemium) nunca exibem o bloqueio.
    if (PUBLIC_ROUTES.includes(normalizeRoutePath(this.router.url))) {
      if (this.isOpen) {
        this.close();
      }
      return;
    }

    if (this.userBlocked) {
      this.isReleased = false;
      this.clearReleaseTimeout();
      this.open();
      return;
    }

    // Mantém o modal montado após a liberação para exibir a confirmação.
    if (wasUserBlocked && this.isOpen && !this.isReleased) {
      this.isReleased = true;
      this.isVerifying = false;
      this.clearVerifyTimeout();
      this.clearReleaseTimeout();
      this.releaseTimeout = setTimeout(() => this.close(), RELEASE_DELAY);
    }
  }

  private finishVerification(): void {
    if (!this.isVerifying) {
      return;
    }

    this.clearVerifyTimeout();
    this.isVerifying = false;
    this.hasVerified = true;
  }

  private open(): void {
    if (this.isOpen) {
      return;
    }

    this.isOpen = true;
    this.lockBodyScroll();
  }

  private close(): void {
    this.isOpen = false;
    this.isReleased = false;
    this.hasVerified = false;
    this.isVerifying = false;
    this.unlockBodyScroll();
  }

  private lockBodyScroll(): void {
    this.previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }

  private unlockBodyScroll(): void {
    document.body.style.overflow = this.previousOverflow || '';
  }

  private clearVerifyTimeout(): void {
    if (this.verifyTimeout) {
      clearTimeout(this.verifyTimeout);
      this.verifyTimeout = null;
    }
  }

  private clearReleaseTimeout(): void {
    if (this.releaseTimeout) {
      clearTimeout(this.releaseTimeout);
      this.releaseTimeout = null;
    }
  }
}
